import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";

import { useSaveRiskAnalysisMatrix } from "@/hooks/api/mutate/useSaveRiskAnalysisMatrix";
import { useBulkInsertRiskAnalysis } from "@/hooks/api/mutate/useBulkInsertRiskAnalysis";
import { useDeleteSingleRiskAnalysis } from "@/hooks/api/mutate/useDeleteSingleRiskAnalysis";
import { useSaveJustifications } from "@/hooks/api/mutate/useSaveJustifications";
import { useGeneratePdf } from "@/hooks/api/mutate/useGeneratePdf";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";

import { useRiskWizardOrchestrator } from "@/hooks/risk-wizard/useRiskWizardOrchestrator";
import { useWizardNavigation } from "@/hooks/risk-wizard/useWizardNavigation";
import { canProceedToNext } from "@/utils/risk-wizard/wizardValidation";

import { RiskWizardLayout } from "@/components/risk-wizard/RiskWizardLayout";
import { ProgressIndicator } from "@/components/ui/progress-indicator";
import { RiskWizardSectorProgress } from "@/components/risk-wizard/RiskWizardSectorProgress";
import { WizardNavigationFooter } from "@/components/risk-wizard/WizardNavigationFooter";
import {
  RISK_WIZARD_STEPS,
  RISK_WIZARD_STEP_SUBLABELS,
} from "@/constants/steps";
import { RiskChangesModal } from "@/components/risk/RiskChangesModal";
import { PaywallDialog } from "@/components/PaywallDialog/PaywallDialog";
import { AutoSeedConfirmDialog } from "@/components/risk-wizard/AutoSeedConfirmDialog";

import { SectorSelectionStep } from "./steps/SectorSelectionStep";
import { SectorMatrixStep } from "./steps/SectorMatrixStep";
import { ResultStep } from "./steps/ResultStep";
import { GenerateReportStep } from "./steps/GenerateReportStep";
import {
  buildSectorProgressItems,
  buildSaveMatrixPayload,
  buildBulkInsertRecords,
} from "./riskWizardPageUtils";

import { generatePDFReport } from "@/utils/generate-report";
import { isRiskAnalysisUnlocked } from "@/utils/guideProgressCalculator";
import LockedSectionNotice from "@/components/shared/LockedSectionNotice";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import { compareSectorAnalysis } from "@/utils/risk-diff";
import { createPartialAnalysis } from "@/utils/risk-partial-save";
import { recalculateAnalysisMetrics } from "@/utils/risk-calculator";
import {
  getNotificareSectorNames,
  getDecizieSectorNames,
  resolveSeedingSectors,
} from "@/utils/sectorMismatch";

import type {
  SectorAnalysis,
  SectorAnalysisDiff,
  SectorJustifications,
} from "@/types/risk-analysis";

export default function RiskWizardPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const orchestrator = useRiskWizardOrchestrator(sessionId ?? "");

  const [showFirstTimeBanner, setShowFirstTimeBanner] = useState(false);
  const [showSeedDialog, setShowSeedDialog] = useState(false);
  const hasAutoInserted = useRef(false);

  const { mutateAsync: saveMatrix } = useSaveRiskAnalysisMatrix();
  const { mutateAsync: bulkInsert } = useBulkInsertRiskAnalysis();
  const { mutateAsync: deleteSingleRiskAnalysis } =
    useDeleteSingleRiskAnalysis();
  const { mutateAsync: saveJustificationsMutation } = useSaveJustifications();

  const notificareSectorNames = useMemo(
    () => getNotificareSectorNames(orchestrator.entityData),
    [orchestrator.entityData]
  );
  const decizieSectorNames = useMemo(
    () => getDecizieSectorNames(orchestrator.decizie),
    [orchestrator.decizie]
  );

  const seedingInfo = useMemo(
    () =>
      resolveSeedingSectors(
        notificareSectorNames,
        decizieSectorNames,
        orchestrator.sectors
      ),
    [notificareSectorNames, decizieSectorNames, orchestrator.sectors]
  );

  useEffect(() => {
    if (hasAutoInserted.current) return;
    if (orchestrator.isLoading) return;
    if (orchestrator.isReturningUser) return;
    if (notificareSectorNames.length === 0 && decizieSectorNames.length === 0)
      return;
    if (orchestrator.sectors.length === 0) return;

    hasAutoInserted.current = true;
    setShowSeedDialog(true);
  }, [
    orchestrator.isLoading,
    orchestrator.isReturningUser,
    notificareSectorNames,
    decizieSectorNames,
    orchestrator.sectors,
  ]);

  const currentAnalysisRef = useRef<SectorAnalysis | null>(null);
  const baseAnalysisRef = useRef<SectorAnalysis | null>(null);

  const handleAnalysisChange = useCallback(
    (analysis: SectorAnalysis | null) => {
      currentAnalysisRef.current = analysis;
    },
    []
  );

  const handleBaseAnalysisChange = useCallback(
    (analysis: SectorAnalysis | null) => {
      baseAnalysisRef.current = analysis;
    },
    []
  );

  const [matrixState, setMatrixState] = useState<{
    isLocked: boolean;
    isDefault: boolean;
    hasChanges: boolean;
  }>({ isLocked: true, isDefault: true, hasChanges: false });
  const [unlockRequested, setUnlockRequested] = useState(false);

  const [allModificationsJustified, setAllModificationsJustified] =
    useState(true);
  const [showResultValidation, setShowResultValidation] = useState(false);

  const justificationsRef = useRef<Record<string, SectorJustifications>>({});

  const initialJustifications = useMemo(() => {
    const map: Record<string, SectorJustifications> = {};
    for (const ra of orchestrator.riskAnalyses) {
      const j = ra.justifications;
      if (j && Object.keys(j).length > 0) {
        map[ra.sector_id] = j;
      }
    }
    return map;
  }, [orchestrator.riskAnalyses]);

  const handleJustificationsChange = useCallback(
    (justifications: Record<string, SectorJustifications>) => {
      justificationsRef.current = justifications;
    },
    []
  );

  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallActionType, setPaywallActionType] = useState<
    "doc_analiza_risc_enire_pdf" | "evaluare_risc_enire"
  >("doc_analiza_risc_enire_pdf");
  const generatePdfMutation = useGeneratePdf();
  const { hasInsufficientCredits: hasInsufficientPdfCredits } =
    useCheckActionCredits(
      {
        session_id: sessionId ?? "",
        action_type: CREDIT_ACTION_TYPES.DOC_ANALIZA_RISC_ENIRE_PDF,
      },
      !!sessionId
    );
  const { data: enireCreditCheck } = useCheckActionCredits(
    {
      session_id: sessionId ?? "",
      action_type: CREDIT_ACTION_TYPES.EVALUARE_RISC_ENIRE,
    },
    !!sessionId
  );
  const { mutateAsync: deductCredits, isPending: isDeductingEnire } =
    useDeductCredits();
  const isStep2CreditActionPending = isDeductingEnire;

  const ensureStep2CreditsPaid = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;
    if (enireCreditCheck?.already_purchased) return true;
    if (enireCreditCheck && !enireCreditCheck.has_credits) {
      setPaywallActionType("evaluare_risc_enire");
      setShowPaywall(true);
      return false;
    }
    try {
      await deductCredits({
        action_type: CREDIT_ACTION_TYPES.EVALUARE_RISC_ENIRE,
        session_id: sessionId,
      });
      return true;
    } catch (error) {
      if (error instanceof InsufficientCreditsError) {
        setPaywallActionType("evaluare_risc_enire");
        setShowPaywall(true);
        return false;
      }
      toast({
        title: "Eroare",
        description: "Nu s-au putut deduce creditele.",
        variant: "destructive",
      });
      return false;
    }
  }, [sessionId, enireCreditCheck, deductCredits, toast]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveDiff, setSaveDiff] = useState<SectorAnalysisDiff | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetDiff, setResetDiff] = useState<SectorAnalysisDiff | null>(null);
  const [resetAnalysis, setResetAnalysis] = useState<SectorAnalysis | null>(
    null
  );

  const handleMatrixStateChange = useCallback(
    (state: { isLocked: boolean; isDefault: boolean; hasChanges: boolean }) => {
      setMatrixState(state);
    },
    []
  );

  const handleUnlockRequestHandled = useCallback(() => {
    setUnlockRequested(false);
  }, []);

  const handleResetHandled = useCallback(() => {
    setResetAnalysis(null);
  }, []);

  const currentStepType =
    orchestrator.wizardSteps.mainSteps[orchestrator.currentMainStep - 1]?.type;

  const canProceed = canProceedToNext(currentStepType, {
    selectedSectorIds: orchestrator.selectedSectorIds,
    riskAnalyses: orchestrator.riskAnalyses,
    skippedSectorIds: orchestrator.skippedSectorIds,
  });

  const currentSectorStep =
    orchestrator.wizardSteps.sectorSteps[orchestrator.currentSectorIndex];

  const currentSectorRa = currentSectorStep
    ? orchestrator.riskAnalyses.find(
        (r) => r.sector_id === currentSectorStep.sectorId
      )
    : undefined;
  const isSectorCompleted = currentSectorRa?.has_populated_matrix === true;
  const isSectorDefault = currentSectorRa?.is_default !== false;

  const navigation = useWizardNavigation({
    currentMainStep: orchestrator.currentMainStep,
    currentSectorIndex: orchestrator.currentSectorIndex,
    sectorSteps: orchestrator.wizardSteps.sectorSteps,
    isSaving: orchestrator.isSaving,
    canProceed,
    isCurrentSectorCompleted: isSectorCompleted,
    setCurrentMainStep: orchestrator.setCurrentMainStep,
    setCurrentSectorIndex: orchestrator.setCurrentSectorIndex,
    onSkipSector: orchestrator.addSkippedSector,
    onComplete: () => {
      toast({
        title: "Finalizat",
        description: "Analiza de risc a fost finalizata cu succes.",
      });
      navigate("/dashboard");
    },
  });

  const handleNavigateToSector = useCallback(
    async (sectorId: string) => {
      const ok = await ensureStep2CreditsPaid();
      if (!ok) return;
      navigation.goToSector(sectorId);
    },
    [ensureStep2CreditsPaid, navigation]
  );

  const nextIsLocked = false;

  const invalidateSession = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.riskAnalysis.bySession(orchestrator.sessionId),
      }),
    [queryClient, orchestrator.sessionId]
  );

  const handleSeedConfirm = useCallback(async () => {
    setShowSeedDialog(false);
    if (seedingInfo.sectorIds.length === 0) return;

    try {
      orchestrator.setIsSaving(true);
      await bulkInsert({
        records: buildBulkInsertRecords(
          orchestrator.sessionId,
          seedingInfo.sectorIds
        ),
      });
      await invalidateSession();
      setShowFirstTimeBanner(true);
    } catch {
      toast({
        title: "Eroare",
        description: "Nu s-au putut salva sectoarele.",
        variant: "destructive",
      });
    } finally {
      orchestrator.setIsSaving(false);
    }
  }, [seedingInfo, orchestrator, bulkInsert, invalidateSession]);

  const handleSeedCancel = useCallback(() => {
    setShowSeedDialog(false);
  }, []);

  const handleAddSectors = useCallback(
    async (sectorIds: string[]) => {
      try {
        orchestrator.setIsSaving(true);
        await bulkInsert({
          records: buildBulkInsertRecords(orchestrator.sessionId, sectorIds),
        });
        await invalidateSession();
      } catch {
        toast({
          title: "Eroare",
          description: "Nu s-au putut adăuga sectoarele.",
          variant: "destructive",
        });
      } finally {
        orchestrator.setIsSaving(false);
      }
    },
    [
      orchestrator.sessionId,
      bulkInsert,
      invalidateSession,
      orchestrator.setIsSaving,
    ]
  );

  const handleRemoveSector = useCallback(
    async (sectorId: string) => {
      const riskAnalysisId = orchestrator.riskAnalysisIdMap[sectorId];
      if (!riskAnalysisId) return;

      try {
        orchestrator.setIsSaving(true);
        await deleteSingleRiskAnalysis({ id: riskAnalysisId });
        await invalidateSession();
      } catch {
        toast({
          title: "Eroare",
          description: "Nu s-a putut șterge sectorul din baza de date.",
          variant: "destructive",
        });
      } finally {
        orchestrator.setIsSaving(false);
      }
    },
    [
      orchestrator.riskAnalysisIdMap,
      orchestrator.setIsSaving,
      deleteSingleRiskAnalysis,
      invalidateSession,
    ]
  );

  const saveCurrentSectorAndAdvance = useCallback(
    async (analysis: SectorAnalysis) => {
      const currentSector =
        orchestrator.wizardSteps.sectorSteps[orchestrator.currentSectorIndex];
      if (!currentSector || !orchestrator.organizationSize) {
        navigation.goNext();
        return;
      }

      try {
        orchestrator.setIsSaving(true);
        const payload = buildSaveMatrixPayload(
          orchestrator.sessionId,
          currentSector.sectorId,
          orchestrator.organizationSize,
          analysis.attackMatrix
        );
        await saveMatrix(payload);
        await invalidateSession();
        navigation.goNext();
      } catch {
        toast({
          title: "Eroare la salvare",
          description: "Nu s-a putut salva matricea de risc.",
          variant: "destructive",
        });
      } finally {
        orchestrator.setIsSaving(false);
      }
    },
    [orchestrator, navigation, saveMatrix, invalidateSession]
  );

  const handleSaveConfirm = useCallback(
    async (selectedChangeIds: Set<string>) => {
      const currentAnalysis = currentAnalysisRef.current;
      const baseAnalysis = baseAnalysisRef.current;
      if (!currentAnalysis || !baseAnalysis || !orchestrator.organizationSize)
        return;

      const analysisToSave = createPartialAnalysis(
        baseAnalysis,
        currentAnalysis,
        selectedChangeIds,
        {
          id: orchestrator.sessionId,
          sessionId: orchestrator.sessionId,
          companyName: orchestrator.companyName,
          cui: orchestrator.cui,
          organizationSize: orchestrator.organizationSize,
          selectedSectors: [],
          source: "existing_analysis",
          createdAt: new Date(),
        }
      );

      setShowSaveModal(false);
      await saveCurrentSectorAndAdvance(analysisToSave);
    },
    [orchestrator, saveCurrentSectorAndAdvance]
  );

  const handleResetClick = useCallback(() => {
    const currentAnalysis = currentAnalysisRef.current;
    const baseAnalysis = baseAnalysisRef.current;
    if (!currentAnalysis || !baseAnalysis) return;

    const diff = compareSectorAnalysis(baseAnalysis, currentAnalysis);
    setResetDiff(diff);
    setShowResetModal(true);
  }, []);

  const handleResetConfirm = useCallback((selectedChangeIds: Set<string>) => {
    const currentAnalysis = currentAnalysisRef.current;
    const baseAnalysis = baseAnalysisRef.current;
    if (!currentAnalysis || !baseAnalysis) return;

    let updatedMatrix = currentAnalysis.attackMatrix.map((cat) => ({
      ...cat,
      actors: { ...cat.actors },
    }));

    for (const changeId of selectedChangeIds) {
      const parts = changeId.split("-");
      const type = parts[0];
      const categoryId = parts[1];
      const actorId = parts[2];

      updatedMatrix = updatedMatrix.map((category) => {
        if (category.categoryId !== categoryId) return category;

        if (type === "impact") {
          const originalCategory = baseAnalysis.attackMatrix.find(
            (c) => c.categoryId === categoryId
          );
          if (!originalCategory) return category;
          return { ...category, impact: originalCategory.impact };
        }

        if (type === "probability" && actorId) {
          const originalCategory = baseAnalysis.attackMatrix.find(
            (c) => c.categoryId === categoryId
          );
          if (!originalCategory) return category;
          const originalActor = originalCategory.actors[actorId];
          if (!originalActor) return category;
          return {
            ...category,
            actors: {
              ...category.actors,
              [actorId]: { ...originalActor },
            },
          };
        }

        return category;
      });
    }

    const { actorTotals, totalScore, cyfunLevel } =
      recalculateAnalysisMetrics(updatedMatrix);

    setResetAnalysis({
      ...currentAnalysis,
      attackMatrix: updatedMatrix,
      actorTotals,
      totalScore,
      cyfunLevel,
    });

    setShowResetModal(false);
  }, []);

  const handleNext = useCallback(async () => {
    const currentStep = orchestrator.currentMainStep;

    if (currentStep === 1) {
      const ok = await ensureStep2CreditsPaid();
      if (!ok) return;

      const firstUncompletedIndex =
        orchestrator.wizardSteps.sectorSteps.findIndex((step) => {
          const ra = orchestrator.riskAnalyses.find(
            (r) => r.sector_id === step.sectorId
          );
          return (
            !ra?.has_populated_matrix &&
            !orchestrator.skippedSectorIds.has(step.sectorId)
          );
        });
      if (firstUncompletedIndex >= 0) {
        orchestrator.setCurrentSectorIndex(firstUncompletedIndex);
      }

      navigation.goNext();
      return;
    }

    if (currentStep === 2) {
      const analysis = currentAnalysisRef.current;
      const baseAnalysis = baseAnalysisRef.current;

      if (!analysis) {
        navigation.goNext();
        return;
      }

      if (baseAnalysis && matrixState.hasChanges) {
        const diff = compareSectorAnalysis(baseAnalysis, analysis);
        if (diff.hasChanges) {
          setSaveDiff(diff);
          setShowSaveModal(true);
          return;
        }
      }

      navigation.goNext();
      return;
    }

    if (currentStep === 3) {
      if (!allModificationsJustified) {
        setShowResultValidation(true);
        return;
      }

      try {
        orchestrator.setIsSaving(true);
        await saveJustificationsMutation({
          session_id: orchestrator.sessionId,
          justifications: justificationsRef.current,
        });
        await invalidateSession();
        navigation.goNext();
      } catch {
        toast({
          title: "Eroare",
          description: "Nu s-au putut salva justificările.",
          variant: "destructive",
        });
      } finally {
        orchestrator.setIsSaving(false);
      }
      return;
    }

    navigation.goNext();
  }, [
    orchestrator,
    navigation,
    matrixState.hasChanges,
    allModificationsJustified,
    saveCurrentSectorAndAdvance,
    invalidateSession,
    saveJustificationsMutation,
    ensureStep2CreditsPaid,
  ]);

  if (
    orchestrator.isLoading ||
    (!orchestrator.isReturningUser && orchestrator.isSaving)
  ) {
    return (
      <RiskWizardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900" />
        </div>
      </RiskWizardLayout>
    );
  }

  if (orchestrator.error || !orchestrator.sessionId) {
    return (
      <RiskWizardLayout>
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
          <p className="text-destructive">Eroare la incarcarea datelor</p>
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            Inapoi la Dashboard
          </Button>
        </div>
      </RiskWizardLayout>
    );
  }

  if (!isRiskAnalysisUnlocked(orchestrator.entityData)) {
    return (
      <RiskWizardLayout>
        <LockedSectionNotice
          title="Secțiune indisponibilă"
          message="Analiza de risc devine disponibilă după finalizarea Formularului de Notificare."
          ctaLabel="Completează Formularul de Notificare"
          ctaTo="/onboarding/formular-notificare"
        />
      </RiskWizardLayout>
    );
  }

  const currentMainStep = orchestrator.currentMainStep;

  function renderStepContent() {
    switch (currentMainStep) {
      case 1:
        return (
          <SectorSelectionStep
            selectedSectorIds={orchestrator.selectedSectorIds}
            sectorAnalysesMap={orchestrator.sectorAnalysesMap}
            companyName={orchestrator.companyName}
            onAddSectors={handleAddSectors}
            onRemoveSector={handleRemoveSector}
            onNavigateToSector={handleNavigateToSector}
            navigateIsPending={isStep2CreditActionPending}
            navigateIsLocked={false}
            showFirstTimeBanner={showFirstTimeBanner}
            onDismissFirstTimeBanner={() => setShowFirstTimeBanner(false)}
            notificareSectorNames={notificareSectorNames}
            decizieSectorNames={decizieSectorNames}
          />
        );
      case 2:
        return currentSectorStep ? (
          <SectorMatrixStep
            key={currentSectorStep.sectorId}
            sectorId={currentSectorStep.sectorId}
            sectorCode={currentSectorStep.sectorCode}
            sectorName={currentSectorStep.sectorName}
            sessionId={orchestrator.sessionId}
            organizationSize={orchestrator.organizationSize}
            serverAnalysis={
              orchestrator.sectorAnalysesMap[currentSectorStep.sectorId] ?? null
            }
            isSectorCompleted={isSectorCompleted}
            isSectorDefault={isSectorDefault}
            onAnalysisChange={handleAnalysisChange}
            onMatrixStateChange={handleMatrixStateChange}
            onBaseAnalysisChange={handleBaseAnalysisChange}
            unlockRequested={unlockRequested}
            onUnlockRequestHandled={handleUnlockRequestHandled}
            resetAnalysis={resetAnalysis}
            onResetHandled={handleResetHandled}
          />
        ) : null;
      case 3:
        return (
          <ResultStep
            sessionId={orchestrator.sessionId}
            companyName={orchestrator.companyName}
            organizationSize={orchestrator.organizationSize}
            globalScore={orchestrator.globalScore}
            globalCyFunLevel={orchestrator.globalCyFunLevel}
            totalModifications={orchestrator.totalModifications}
            riskAnalyses={orchestrator.riskAnalyses}
            sectorAnalysesMap={orchestrator.sectorAnalysesMap}
            skippedSectorIds={orchestrator.skippedSectorIds}
            sectors={orchestrator.sectors}
            initialJustifications={initialJustifications}
            onJustificationsChange={handleJustificationsChange}
            onEditSector={navigation.goToSector}
            onAllModificationsJustified={setAllModificationsJustified}
            showValidation={showResultValidation}
          />
        );
      case 4: {
        const analyzedCount = orchestrator.riskAnalyses.filter(
          (ra) => !orchestrator.skippedSectorIds.has(ra.sector_id)
        ).length;
        return (
          <GenerateReportStep
            companyName={orchestrator.companyName}
            globalScore={orchestrator.globalScore}
            globalCyFunLevel={orchestrator.globalCyFunLevel}
            totalModifications={orchestrator.totalModifications}
            analyzedSectorsCount={analyzedCount}
            entityContactData={orchestrator.entityData?.contact_data ?? null}
            isPdfGenerating={generatePdfMutation.isPending}
            hasInsufficientPdfCredits={hasInsufficientPdfCredits}
            onDownloadPdf={() => {
              if (!sessionId) return;
              if (hasInsufficientPdfCredits) {
                setPaywallActionType("doc_analiza_risc_enire_pdf");
                setShowPaywall(true);
                return;
              }

              generatePdfMutation.mutate(
                { sessionId, type: "riskAnalysis" },
                {
                  onSuccess: async (data) => {
                    await generatePDFReport(
                      data as unknown as {
                        template: unknown[];
                        inputs: unknown[];
                        type: "merge" | undefined;
                      }
                    );
                  },
                  onError: (error) => {
                    if (error instanceof InsufficientCreditsError) {
                      setShowPaywall(true);
                      return;
                    }
                    toast({
                      title: "Eroare",
                      description:
                        "A apărut o eroare la generarea raportului PDF.",
                      variant: "destructive",
                    });
                  },
                }
              );
            }}
          />
        );
      }
      default:
        return null;
    }
  }

  const sectorProgressItems = buildSectorProgressItems(
    orchestrator.wizardSteps.sectorSteps,
    orchestrator.currentSectorIndex
  );

  return (
    <RiskWizardLayout
      companyName={orchestrator.companyName}
      narrow={currentMainStep !== 2}
      mainProgressSlot={
        <ProgressIndicator
          steps={RISK_WIZARD_STEPS}
          stepSublabels={RISK_WIZARD_STEP_SUBLABELS}
          currentStep={currentMainStep - 1}
          onStepClick={(index) => orchestrator.setCurrentMainStep(index + 1)}
          firstStepClickable
        />
      }
      sectorProgressSlot={
        currentMainStep === 2 && sectorProgressItems.length > 0 ? (
          <RiskWizardSectorProgress
            sectors={sectorProgressItems}
            currentIndex={orchestrator.currentSectorIndex}
            onPrevSector={() =>
              navigation.navigateToSectorIndex(
                orchestrator.currentSectorIndex - 1
              )
            }
            onNextSector={() =>
              navigation.navigateToSectorIndex(
                orchestrator.currentSectorIndex + 1
              )
            }
            onSectorChange={(index: number) =>
              navigation.navigateToSectorIndex(index)
            }
          />
        ) : undefined
      }
      footerSlot={
        <WizardNavigationFooter
          currentMainStep={currentMainStep}
          isLastSector={navigation.isLastSector}
          canGoBack={navigation.canGoBack}
          canGoNext={navigation.canGoNext}
          isSaving={orchestrator.isSaving}
          onBack={navigation.goBack}
          onNext={handleNext}
          hideNextButton={currentMainStep === 4}
          nextIsLocked={nextIsLocked}
          hasStartedAnalysis={orchestrator.riskAnalyses.some(
            (ra) => ra.has_populated_matrix === true
          )}
          onSkip={
            currentMainStep === 2 && !isSectorCompleted
              ? navigation.skipSector
              : undefined
          }
          matrixIsLocked={
            currentMainStep === 2 ? matrixState.isLocked : undefined
          }
          matrixIsDefault={
            currentMainStep === 2 ? matrixState.isDefault : undefined
          }
          matrixHasChanges={
            currentMainStep === 2 ? matrixState.hasChanges : undefined
          }
          onRequestUnlock={
            currentMainStep === 2 && matrixState.isLocked
              ? () => setUnlockRequested(true)
              : undefined
          }
          onSaveDefaults={
            currentMainStep === 2 && matrixState.isLocked && !isSectorCompleted
              ? () => {
                  const analysis = currentAnalysisRef.current;
                  if (analysis) {
                    saveCurrentSectorAndAdvance(analysis);
                  }
                }
              : undefined
          }
          onReset={
            currentMainStep === 2 &&
            !matrixState.isLocked &&
            !matrixState.isDefault
              ? handleResetClick
              : undefined
          }
        />
      }
    >
      {renderStepContent()}

      {baseAnalysisRef.current && currentAnalysisRef.current && (
        <>
          <RiskChangesModal
            mode="save"
            open={showSaveModal}
            onOpenChange={setShowSaveModal}
            diff={saveDiff}
            originalAnalysis={baseAnalysisRef.current}
            currentAnalysis={currentAnalysisRef.current}
            onConfirm={handleSaveConfirm}
            isSaving={orchestrator.isSaving}
          />
          <RiskChangesModal
            mode="reset"
            open={showResetModal}
            onOpenChange={setShowResetModal}
            diff={resetDiff}
            originalAnalysis={baseAnalysisRef.current}
            currentAnalysis={currentAnalysisRef.current}
            onConfirm={handleResetConfirm}
          />
        </>
      )}

      <PaywallDialog
        open={showPaywall}
        onOpenChange={setShowPaywall}
        actionType={paywallActionType}
      />

      <AutoSeedConfirmDialog
        open={showSeedDialog}
        onConfirm={handleSeedConfirm}
        onCancel={handleSeedCancel}
        sectorNames={seedingInfo.sectorNames}
        source={seedingInfo.source}
        mismatch={seedingInfo.mismatch}
      />
    </RiskWizardLayout>
  );
}
